import { createSelector } from 'reselect';
import get from 'lodash/get';
import flatMap from 'lodash/fp/flatMap';
import pipe from 'lodash/fp/pipe';
import filter from 'lodash/fp/filter';
import isNil from 'lodash/fp/isNil';
import cond from 'lodash/cond';
import stubTrue from 'lodash/stubTrue';
import createCachedSelector from 're-reselect';

import { getRootNodeId, getForm } from 'common/entities/form/selectors';
import {
    getFormNode,
    getFormNodes,
    getFormNodeByName,
    getAllFormNodes,
    getQuestionFormNodes,
    getFormNodeConfigValue,
} from 'common/entities/formNode/selectors';
import { ENTITIES_KEY_NODE } from 'common/entities/formNode';
import { ENTITIES_KEY_FORM } from 'common/entities/form';
import { ENTITIES_KEY_TICKET_TYPES } from 'common/entities/ticketType';
import {
    NODE_NAME_ATTENDING,
    NODE_NAME_ADDITIONAL_GUEST_TOP_GROUP,
    NODE_NAME_ADDITIONAL_GUEST_COUNT,
    NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP,
    NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP_REGEXP,
    NODE_NAME_ADDITIONAL_GUEST_TEMPLATE,
    NODE_NAME_UTM_PARAMS,
    NODE_NAME_CHECK_IN,
    NODE_NAME_CHECK_OUT,
    NODE_NAME_CAPTCHA,
    NODE_NAME_FIRST_NAME,
    NODE_NAME_LAST_NAME,
    NODE_NAME_EMAIL,
    NODE_NAME_ADDITIONAL_GUEST_FIRST_NAME,
    NODE_NAME_ADDITIONAL_GUEST_LAST_NAME,
    NODE_NAME_ADDITIONAL_GUEST_EMAIL,
    NODE_NAME_ADDITIONAL_GUEST_FIRST_NAME_PATTERN,
    NODE_NAME_ADDITIONAL_GUEST_LAST_NAME_PATTERN,
    NODE_NAME_ADDITIONAL_GUEST_EMAIL_PATTERN,
    NODE_NAME_CC_EMAIL,
    NODE_NAME_BCC_EMAIL,
    NODE_NAME_ADDITIONAL_GUEST_PREFIX_REGEXP,
    NODE_NAME_SOCIAL_RSVP,
} from 'common/entities/formNode/nodeNames';

import { INPUT_TYPE_EMAIL, INPUT_TYPE_HIDDEN } from 'common/entities/formNode/inputTypes';
import {
    NODE_TYPE_GROUP,
    NODE_TYPE_QUESTION,
    NODE_TYPE_QUESTION_MULTIPLE,
    NODE_TYPE_CONTENT,
} from 'common/entities/formNode/nodeTypes';

import reducerNamespace from '../constants/reducerNamespace';
import { INPUT_TYPES, FORM_CONFIG } from '../constants/bootstrapKeys';
import { VISIBILITY_LOCKED_NODE_NAMES, TOP_BLOCKED_NODES, DUP_LOCKED_NODE_NAMES } from '../constants/nodeNameRules';
import { validateFormData } from '../utils/formValidations';

export const getFormBuilder = (state) => state[reducerNamespace];

export const getMounted = (state) => get(getFormBuilder(state), ['app', 'mounted'], false);
export const getEventFormEndpointsEnabled = (state) =>
    get(getFormBuilder(state), ['app', 'eventFormEndpointsEnabled'], false);
export const getEventFormId = (state) => get(getFormBuilder(state), ['app', 'eventFormId']);
export const getEditLogicFormNodeId = (state) => getFormBuilder(state).logicModal.editNodeLogicId;
export const getBackupLogicFormNodeId = (state) => getFormBuilder(state).logicModal.backupNodeLogicId;
/**
 * @param  {Object} state
 * @return {String|undefined}
 */
export const getFormId = (state) => get(getFormBuilder(state), 'app.formId');

export const getEditEntityId = (state) => getFormBuilder(state).app.editEntityId;
export const getEditEntityType = (state) => getFormBuilder(state).app.editEntityType;
export const getActiveFormEditView = (state) => getFormBuilder(state).app.activeFormEditView;
export const getFormErrors = (state) => getFormBuilder(state).app.errors;

export const getFormLiveErrors = (state) => getFormBuilder(state).app.liveErrors;

export const getFreemiumSelectedModal = (state) => getFormBuilder(state).app.freemiumSelectedModal;

export const getSavingQuestionToBank = (state) => getFormBuilder(state).app.savingQuestionToBank;

export const getFormNodeParent = (state, formNode) => {
    const parentId = formNode.parent;
    return parentId ? getFormNode(state, parentId) : null;
};

const reduceFormChildren = (formNodes, children) =>
    children.reduce((prev, next) => {
        const formNode = formNodes.find((e) => e.id === next);
        if (formNode.type === NODE_TYPE_GROUP && formNode.name !== NODE_NAME_ADDITIONAL_GUEST_TOP_GROUP) {
            const newChildren = reduceFormChildren(formNodes, formNode.children);
            return [...prev, ...newChildren];
        } else if (formNode.name === NODE_NAME_ADDITIONAL_GUEST_TOP_GROUP) {
            const bringingGuestNode = formNodes.find((node) => node.name === NODE_NAME_ADDITIONAL_GUEST_COUNT);
            return [...prev, bringingGuestNode];
        } else if (formNode.type === NODE_TYPE_QUESTION) {
            return [...prev, formNode];
        }
        return prev;
    }, []);

export const getOrderedOperandNodes = createCachedSelector(
    [getRootNodeId, getFormNodes],
    (formRootNodeId, formNodes) => {
        const formRootNode = formNodes.find((e) => e.id === formRootNodeId);
        return reduceFormChildren(formNodes, formRootNode.children);
    },
)((state, formId) => formId);

export const getFormContentTypeNodes = createSelector([getFormNodes], (formNodes) =>
    formNodes.filter((formNode) => formNode && formNode.type === NODE_TYPE_CONTENT),
);

export const getFormIsSaving = (state, formId) => !!get(getFormBuilder(state), `app.savingForms.${formId}`);

export const getFormIsLoading = (state, formId) => !!getFormBuilder(state).app.loadingForms[formId];

export const getEditFormNodeId = (state) => {
    const editEntityId = getEditEntityId(state);
    const editEntityType = getEditEntityType(state);
    return editEntityType === ENTITIES_KEY_NODE ? editEntityId : undefined;
};

export const getEditTicketTypeEntity = (state) => {
    const editEntityId = getEditEntityId(state);
    const editEntityType = getEditEntityType(state);
    return editEntityType === ENTITIES_KEY_TICKET_TYPES ? editEntityId : undefined;
};

export const getEditFormNode = (state) => {
    const editFormNodeId = getEditFormNodeId(state);
    return editFormNodeId ? getFormNode(state, editFormNodeId) : undefined;
};

export const getEditFormNodeLabel = (state) => {
    const editFormNode = getEditFormNode(state);

    return get(editFormNode, 'label');
};

export const getEditFormNodeName = (state) => {
    const editFormNode = getEditFormNode(state);

    return get(editFormNode, 'name');
};

export const getEditForm = (state) => {
    const editEntityId = getEditEntityId(state);
    const editEntityType = getEditEntityType(state);
    return editEntityType === ENTITIES_KEY_FORM ? getForm(state, editEntityId) : undefined;
};

export const getEditNodeOptions = createSelector([getEditFormNode], (editNode) => {
    const options = get(editNode, 'config.options', {
        result: [],
        entities: {},
    });
    return options.result.map((id) => ({ ...options.entities[id], id }));
});

export const getIsEntityBeingEdited = (state) =>
    !!(getEditFormNode(state) || getEditForm(state) || getEditTicketTypeEntity(state));

export const getEditLogicFormNode = (state) => {
    const editLogicNodeId = getEditLogicFormNodeId(state);
    return getFormNode(state, editLogicNodeId);
};
export const getBackupLogicFormNode = (state) => {
    const backupLogicFormNodeId = getBackupLogicFormNodeId(state);
    return getFormNode(state, backupLogicFormNodeId);
};

const hasNodeByName = (nodeName) => (state, formId) => !!getFormNodeByName(state, formId, nodeName);

/**
 * @param {Object} state
 * @param {string} formId
 * @return {string[]}
 */
export const getAlreadyAddedUniqueNames = createCachedSelector(
    [
        hasNodeByName(NODE_NAME_ATTENDING),
        hasNodeByName(NODE_NAME_ADDITIONAL_GUEST_COUNT),
        hasNodeByName(NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP),
        hasNodeByName(NODE_NAME_UTM_PARAMS),
        hasNodeByName(NODE_NAME_CHECK_IN),
        hasNodeByName(NODE_NAME_CHECK_OUT),
        hasNodeByName(NODE_NAME_CAPTCHA),
        hasNodeByName(NODE_NAME_ADDITIONAL_GUEST_FIRST_NAME),
        hasNodeByName(NODE_NAME_ADDITIONAL_GUEST_LAST_NAME),
        hasNodeByName(NODE_NAME_ADDITIONAL_GUEST_EMAIL),
        hasNodeByName(NODE_NAME_SOCIAL_RSVP),
    ],
    (
        hasRootAttending,
        hasAdditionalGuestCountQuestion,
        hasAdditionalGuestInfoGroup,
        hasUtmParamsQuestionGroup,
        hasCheckInQuestion,
        hasCheckOutQuestion,
        hasCaptchaQuestion,
        hasAdditionalGuestFirstName,
        hasAdditionalGuestLastName,
        hasAdditionalGuestEmail,
        hasSocialRsvp,
    ) =>
        [
            hasRootAttending && NODE_NAME_ATTENDING,
            (hasAdditionalGuestCountQuestion || hasAdditionalGuestInfoGroup) && NODE_NAME_ADDITIONAL_GUEST_TEMPLATE,
            hasUtmParamsQuestionGroup && NODE_NAME_UTM_PARAMS,
            hasCheckInQuestion && NODE_NAME_CHECK_IN,
            hasCheckOutQuestion && NODE_NAME_CHECK_OUT,
            hasCaptchaQuestion && NODE_NAME_CAPTCHA,
            hasAdditionalGuestFirstName && NODE_NAME_ADDITIONAL_GUEST_FIRST_NAME_PATTERN,
            hasAdditionalGuestLastName && NODE_NAME_ADDITIONAL_GUEST_LAST_NAME_PATTERN,
            hasAdditionalGuestEmail && NODE_NAME_ADDITIONAL_GUEST_EMAIL_PATTERN,
            hasSocialRsvp && NODE_NAME_SOCIAL_RSVP,
            NODE_NAME_FIRST_NAME,
            NODE_NAME_LAST_NAME,
            NODE_NAME_EMAIL,
        ].filter((v) => v),
)((state, formId) => formId);

export const getBootstrap = (state) => getFormBuilder(state).app.bootstrap;

/**
 * @param {Object}
 * @return {Object[]} - SUI options format
 */
const getInputTypeOptions = (state) => {
    const formBuilderBootstrap = getBootstrap(state);
    return formBuilderBootstrap[INPUT_TYPES];
};

const getAllowedInputTypeOptionsForCcEmailNode = createSelector(getInputTypeOptions, (inputTypeOptions) =>
    inputTypeOptions.filter(({ value }) => [INPUT_TYPE_EMAIL, INPUT_TYPE_HIDDEN].includes(value)),
);

const getAllowedInputTypeOptionsForBccEmailNode = createSelector(getInputTypeOptions, (inputTypeOptions) =>
    inputTypeOptions.filter(({ value }) => [INPUT_TYPE_EMAIL, INPUT_TYPE_HIDDEN].includes(value)),
);

/**
 * @param {Object} state
 * @param {string} formNodeName
 * @return {Object[]} - SUI options format
 */
export const getQuestionTypeOptions = (state, formNodeName) =>
    cond([
        [() => formNodeName === NODE_NAME_CC_EMAIL, () => getAllowedInputTypeOptionsForCcEmailNode(state)],
        [() => formNodeName === NODE_NAME_BCC_EMAIL, () => getAllowedInputTypeOptionsForBccEmailNode(state)],
        [stubTrue, () => getInputTypeOptions(state)],
    ])();

export const isBootstrapLoading = (state) => !!getFormBuilder(state).app.bootstrapLoading;
export const isBootstrapLoaded = (state) => {
    if (isBootstrapLoading(state)) {
        return false;
    }
    const bootstrap = getFormBuilder(state).app.bootstrap || {};
    const keys = Object.keys(bootstrap);
    return typeof keys[0] !== 'undefined' && Object.keys(bootstrap[keys[0]]).length !== 0;
};
export const getInputTypeDisplayNames = (state) => getBootstrap(state).inputTypeDisplayNames;

export const getInputTypeDisplayName = (key) => (state) => getInputTypeDisplayNames(state)[key];

export const getDragNodeId = (state) => getFormBuilder(state).dragging.dragNodeId;
export const getDropParentNodeId = (state) => getFormBuilder(state).dragging.dropParentNodeId;
export const getDropNodeId = (state) => getFormBuilder(state).dragging.dropNodeId;
export const getDropNodePosition = (state) => getFormBuilder(state).dragging.dropNodePosition;
export const getDragNodePosition = (state) => getFormBuilder(state).dragging.dragNodePosition;

export const getActiveSidebarTab = (state) => getFormBuilder(state).app.activeSidebarTab;

export const getUserFlags = (state) => getBootstrap(state).flags;

export const getUserFlag = (flag) => (state) => {
    const flags = getUserFlags(state);
    return flags ? flags[flag] : true;
};

export const getStylesFlag = (state) => getUserFlag('formStylesCanUse')(state);

export const getLogicFlag = (state) => getUserFlag('formLogicCanUse')(state);

export const getLibraryFlag = (state) => getUserFlag('formLibraryCanCreate')(state);

export const getResponseLimitFlag = (state) => getUserFlag('formResponseLimitCanUse')(state);

export const getNodeLockingAllowed = (state) => getUserFlag('nodeLockingAllowed')(state);

export const getAllow20AdditionalGuest = (state) => getUserFlag('allow20AdditionalGuests')(state);

export const getOpenStylingSection = (state) => getFormBuilder(state).app.openStylingSection;

export const getOpenTicketStylingSection = (state) => getFormBuilder(state).app.openTicketStylingSection;

export const getOpenTicketStylingType = (state) => getFormBuilder(state).app.editEntityTicketType;

export const getAdditionalGuestInfoNodes = (state, formId, crossGroupId) =>
    pipe(
        filter((node) => node.name === NODE_NAME_ADDITIONAL_GUEST_TOP_GROUP),
        flatMap((node) => (node.children || []).map((nodeId) => getFormNode(state, nodeId))),
        flatMap((node) => (node.children || []).map((nodeId) => getFormNode(state, nodeId))),
        filter((node) => node.crossGroupId && node.crossGroupId === crossGroupId),
    )(getAllFormNodes(state, formId));

export const getAdditionalGuestGroupCountNode = (state) => {
    const formId = getFormId(state);

    return getFormNodeByName(state, formId, NODE_NAME_ADDITIONAL_GUEST_COUNT);
};

export const getAdditionalGuestGroup = (state, formId) =>
    getFormNodeByName(state, formId, NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP);

export const isAdditionalGuestInfoNode = (state, formId, nodeId) => {
    const additionalGuestInfoNode = getFormNodeByName(state, formId, NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP);

    return additionalGuestInfoNode && additionalGuestInfoNode.children.includes(nodeId);
};

/**
 * Returns true if the given node is inside an additional guest information group.
 * @param {Object} state
 * @param {string} nodeId
 * @return {boolean}
 */
const isInsideAdditionalGuestInfoGroup = (state, nodeId) => {
    const node = getFormNode(state, nodeId) || {};
    const parentNode = getFormNode(state, node.parent) || {};

    return NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP_REGEXP.test(parentNode.name);
};

export const getBlockedNodes = (state, formId) => {
    const blockedNodes = TOP_BLOCKED_NODES.map((blockedNodeName) =>
        getFormNodeByName(state, formId, blockedNodeName),
    ).filter(Boolean);

    return blockedNodes;
};

// TODO: Improve this selector, it's not performant
export const getCustomNodes = (state, formId) => {
    const nodes = getFormNodes(state, formId);

    return nodes.filter(
        (node) =>
            [NODE_TYPE_QUESTION, NODE_TYPE_QUESTION_MULTIPLE].includes(node.type) &&
            !DUP_LOCKED_NODE_NAMES.includes(node.name),
    );
};

/**
 * @param {Object} state
 * @param {string} formId
 * @return {string[]} - form node IDs for the groups of additional guest questions
 *                      (1 per additional guest allowed)
 */
export const getAdditionalGuestGroupNodeIds = (state, formId) => {
    const additionalGuestGroup = getFormNodeByName(state, formId, NODE_NAME_ADDITIONAL_GUEST_TOP_GROUP);
    const additionalGuestGroupNodeIds = get(additionalGuestGroup, 'children', []);

    return additionalGuestGroupNodeIds.filter((childId) => {
        const node = getFormNode(state, childId);

        return node && node.name !== NODE_NAME_ADDITIONAL_GUEST_COUNT;
    });
};

export const getAdditionalGuestGroupNodesLength = createSelector(
    getAdditionalGuestGroupNodeIds,
    (additionalGuestGroupNodes) => additionalGuestGroupNodes.length,
);

/**
 * @param state
 * @param formNodeId
 * @return boolean
 */
export const getCheckedValue = createSelector(getFormNode, (node) => get(node, 'config.checked', false));

/**
 * We need this in some cases when one node lead to another
 * for instance in the additional guest group we want
 * to show in the UI the bringin guest node question to edit
 */
export const getMappedNode = createSelector(
    getFormNode,
    getAdditionalGuestGroupCountNode,
    (formNode, additionalGuestGroupCountNode) => {
        const mappedNodesByName = {
            [NODE_NAME_ADDITIONAL_GUEST_TOP_GROUP]: additionalGuestGroupCountNode,
            default: formNode,
        };

        const nodeToOpen = mappedNodesByName[formNode.name] || mappedNodesByName.default;

        return nodeToOpen;
    },
);

export const getMappedNodeLabel = createSelector(getMappedNode, (node) => node.label);

export const getFormNodeWithMappedLabel = createCachedSelector(
    [getFormNode, getMappedNodeLabel],
    (formNode, formNodeLabel) => ({ ...formNode, label: formNodeLabel }),
)((state, formNodeId) => formNodeId);

export const isAllowedLogicNode = (state, formNode) => {
    const isOnLockedNames = formNode && VISIBILITY_LOCKED_NODE_NAMES.find((name) => name === formNode.name);
    return !isOnLockedNames;
};

const getFormConfig = (state) => {
    const formBuilderBootstrap = getBootstrap(state);
    return formBuilderBootstrap[FORM_CONFIG];
};

export const getSocialNetworkFields = createSelector(
    getFormConfig,
    (_, socialNetwork) => socialNetwork,
    (formConfig, socialNetwork) =>
        get(formConfig, ['inputTypes', 'splashSocialRsvp', 'networks', socialNetwork, 'fields'], []),
);

export const getMultipleFormNodesConfig = (state, nodes, configField) => {
    const nodesConfig = nodes.map((node) => getFormNodeConfigValue(state, node.id, configField));

    const areConfigsEqual = nodesConfig.every((nodeConfigField, index, config) => nodeConfigField === config[0]);

    return areConfigsEqual ? nodesConfig.find((nodeConfig) => !isNil(nodeConfig)) : null;
};

/**
 * Returns all the operandNodes available for a given node when opening the formLogic modal.
 * This is really useful for guest nodes, as it's direct siblings will come in the array so
 * logic wont be mixed between guests.
 *
 * @param {Object} state
 * @param {string} formId
 * @param {string} formNodeId
 * @return {Array}
 */
export const getOperandFormNodesForNode = (state, formId, formNodeId) => {
    const formNode = getFormNode(state, formNodeId);
    const questionNodes = getQuestionFormNodes(state, formId);

    const mainGuestQuestionNodes = questionNodes.filter(
        (operandNode) =>
            // Exclude questions inside additional guest groups.
            !isInsideAdditionalGuestInfoGroup(state, operandNode.id) &&
            // Exclude special additional guest special questions.
            !NODE_NAME_ADDITIONAL_GUEST_PREFIX_REGEXP.test(operandNode.name) &&
            // Exclude children of this node.
            !(formNode.children || []).includes(operandNode.id),
    );

    const parentNode = getFormNodeParent(state, formNode) || {};
    const isAdditionalGuest = parentNode.name === NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP;

    const additionalGuestNodes = isAdditionalGuest
        ? (parentNode.children || []).map((childrenId) => getFormNode(state, childrenId))
        : [];

    const additionalGuestNodesWithoutMainNode = additionalGuestNodes.filter((node) => node.code !== formNode.code);

    const operandNodes = [...mainGuestQuestionNodes, ...additionalGuestNodesWithoutMainNode].filter(
        (node) => node.id !== formNode.id,
    ); // Exclude itself

    return operandNodes;
};

export const getValidationErrors = (state) => {
    const formId = getFormId(state);
    const formNodes = getFormNodes(state, formId);
    const liveErrors = getFormLiveErrors(state);
    const errors = validateFormData(formNodes, liveErrors);
    return errors;
};

export const getIsFormBuilderPristine = (state) => get(getFormBuilder(state), ['app', 'isPristine'], true);
